/**
 * Parsing utilities for interactive blocks (```question / ```decision)
 * embedded in AI chat messages as fenced code blocks.
 */

/** Regex matching a complete interactive block: ```question\n{json}\n``` or ```decision\n{json}\n``` */
const BLOCK_REGEX = /```(?:question|decision)\s*\n[\s\S]*?```/g;

export interface InteractiveBlock {
  type: 'question' | 'decision';
  question: string;
  options: string[];
  multiSelect?: boolean;
}

/**
 * Parse a fenced code block into an InteractiveBlock if it's a `question` or `decision`.
 * Called from ReactMarkdown's `code` component override.
 *
 * @param className - The className from ReactMarkdown (e.g. "language-question")
 * @param codeContent - The raw text inside the code block
 * @returns Parsed block or null if not an interactive block / parse failure
 */
export function parseInteractiveBlock(
  className: string | undefined,
  codeContent: string,
): InteractiveBlock | null {
  const lang = className?.replace('language-', '');
  if (lang !== 'question' && lang !== 'decision') return null;

  try {
    const parsed = JSON.parse(codeContent);
    if (!parsed.question || !Array.isArray(parsed.options)) return null;
    return {
      type: lang,
      question: parsed.question,
      options: parsed.options,
      ...(parsed.multiSelect && { multiSelect: true }),
    };
  } catch {
    return null;
  }
}

/**
 * Count how many interactive blocks are in a message's content.
 */
export function countInteractiveBlocks(content: string): number {
  const matches = content.match(BLOCK_REGEX);
  return matches ? matches.length : 0;
}

/**
 * Return the message content up to (but not including) the Nth interactive block.
 * Used to show only answered blocks inline; the pending block renders in ChatInput.
 */
export function getContentUpToBlock(content: string, blockIndex: number): string {
  let count = 0;
  let lastEnd = 0;
  const regex = new RegExp(BLOCK_REGEX.source, 'g');
  let match: RegExpExecArray | null;

  while ((match = regex.exec(content)) !== null) {
    if (count >= blockIndex) {
      // Return content up to this block's start
      return content.substring(0, match.index);
    }
    lastEnd = match.index + match[0].length;
    count++;
  }

  return content.substring(0, lastEnd);
}

/**
 * Get the Nth interactive block from message content (0-indexed).
 * Returns null if the block doesn't exist or can't be parsed.
 */
export function getInteractiveBlockAt(
  content: string,
  blockIndex: number,
): InteractiveBlock | null {
  const regex = new RegExp(BLOCK_REGEX.source, 'g');
  let match: RegExpExecArray | null;
  let count = 0;

  while ((match = regex.exec(content)) !== null) {
    if (count === blockIndex) {
      const blockText = match[0];
      // Extract language and content from the fenced block
      const langMatch = blockText.match(/^```(question|decision)\s*\n/);
      if (!langMatch) return null;
      const lang = langMatch[1] as 'question' | 'decision';
      const innerContent = blockText
        .substring(langMatch[0].length)
        .replace(/\n?```$/, '')
        .trim();

      try {
        const parsed = JSON.parse(innerContent);
        if (!parsed.question || !Array.isArray(parsed.options)) return null;
        return {
          type: lang,
          question: parsed.question,
          options: parsed.options,
          ...(parsed.multiSelect && { multiSelect: true }),
        };
      } catch {
        return null;
      }
    }
    count++;
  }

  return null;
}
